// Milestone — Yubiley (jubilee) paper-invitation catalog card.
//
// A formal jubilee preview: outer frame is a metallic ring (silver/platinum
// gradient drawn from the template accent + bg), with a fine inner hairline.
// A laurel-wreath SVG medallion holds the Roman numeral "XXV" (with a photo
// optionally tucked inside the wreath). Eyebrow reads YUBILEY; names sit in
// the template's display font; small-caps date + venue close the card.
//
// All colors come from LayoutCtx.
import { Photo, type LayoutCtx } from "./_shared";

export function MilestoneLayout({ names, date, venue, accent, ink, bg, font, photo }: LayoutCtx) {
  const FOIL = `linear-gradient(135deg, ${accent} 0%, ${bg} 22%, ${accent}aa 45%, ${bg} 65%, ${accent} 100%)`;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55, inset 0 0 0 4px ${bg}, inset 0 0 0 5px ${ink}22`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          YUBILEY
        </div>

        {/* Laurel medallion */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          {/* Photo or fallback inside a circular plate */}
          <div
            style={{
              position: "absolute",
              inset: "14%",
              borderRadius: "50%",
              overflow: "hidden",
              border: `1px solid ${accent}88`,
              background: `radial-gradient(circle at 50% 40%, ${accent}22, ${bg})`,
            }}
          >
            <Photo
              src={photo}
              sepia
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
              fallback={
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    placeItems: "center",
                    fontFamily: font,
                    fontSize: 30,
                    letterSpacing: "0.2em",
                    color: accent,
                  }}
                >
                  XXV
                </div>
              }
            />
            {photo && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(180deg, transparent 50%, ${ink}33 100%)`,
                  mixBlendMode: "multiply",
                }}
              />
            )}
          </div>
          {/* Laurel wreath surrounding the plate */}
          <svg viewBox="0 0 100 100" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} aria-hidden>
            {/* Left branch */}
            <g stroke={accent} strokeWidth="0.8" fill={accent} fillOpacity={0.7} opacity={0.9}>
              <path d="M14 50 Q 14 30 30 16" strokeLinecap="round" fill="none" />
              {Array.from({ length: 6 }).map((_, i) => {
                const t = i / 5;
                const cx = 14 + (30 - 14) * t;
                const cy = 50 - 20 * t * (1 - t * 0.4) - 14 * t;
                return (
                  <ellipse
                    key={`l${i}`}
                    cx={cx - 3}
                    cy={cy}
                    rx="3.5"
                    ry="1.4"
                    transform={`rotate(${-30 - i * 6} ${cx - 3} ${cy})`}
                  />
                );
              })}
            </g>
            {/* Right branch (mirrored) */}
            <g stroke={accent} strokeWidth="0.8" fill={accent} fillOpacity={0.7} opacity={0.9}>
              <path d="M86 50 Q 86 30 70 16" strokeLinecap="round" fill="none" />
              {Array.from({ length: 6 }).map((_, i) => {
                const t = i / 5;
                const cx = 86 - (86 - 70) * t;
                const cy = 50 - 20 * t * (1 - t * 0.4) - 14 * t;
                return (
                  <ellipse
                    key={`r${i}`}
                    cx={cx + 3}
                    cy={cy}
                    rx="3.5"
                    ry="1.4"
                    transform={`rotate(${30 + i * 6} ${cx + 3} ${cy})`}
                  />
                );
              })}
            </g>
            {/* Crown star at the top */}
            <path
              d="M50 6 L 52 12 L 58 12 L 53 16 L 55 22 L 50 18 L 45 22 L 47 16 L 42 12 L 48 12 Z"
              fill={accent}
              opacity={0.85}
            />
            {/* Ribbon hint at the bottom */}
            <path d="M40 92 L 50 88 L 60 92 L 56 96 L 50 94 L 44 96 Z" fill={accent} opacity={0.6} />
          </svg>
        </div>

        {/* Names */}
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 28,
            lineHeight: 1.05,
            fontWeight: 400,
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          YUBILEY · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
